import { ArrowLeft } from 'lucide-react';
import { FormEvent, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { post, readErrorMessage } from '../api';
import { useAuth } from '../context/AuthContext';

type LoginResponse = {
    token: string;
    user: {
        id: number;
        username: string;
        created_at: string;
        updated_at: string;
    };
};

export default function Login() {
    const navigate = useNavigate();
    const { isAuthenticated, login } = useAuth();

    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [isRegisterMode, setIsRegisterMode] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [message, setMessage] = useState<string | null>('Sign in to continue to your workspace.');

    useEffect(() => {
        if (isAuthenticated) navigate('/');
    }, [isAuthenticated, navigate]);

    const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault();
        const cleanedUsername = username.trim().toLowerCase();
        if (!cleanedUsername || !password) return;

        setIsSubmitting(true);
        setError(null);
        setMessage(null);

        try {
            if (isRegisterMode) {
                const registerResponse = await post('/auth/register', { username: cleanedUsername, password });
                if (!registerResponse.ok) {
                    throw new Error(await readErrorMessage(registerResponse));
                }
            }

            const loginResponse = await post('/auth/login', { username: cleanedUsername, password });
            if (!loginResponse.ok) {
                throw new Error(await readErrorMessage(loginResponse));
            }

            const payload = (await loginResponse.json()) as LoginResponse;
            login(payload.token, payload.user);
            navigate('/');
        } catch (requestError) {
            setError(requestError instanceof Error ? requestError.message : 'Failed to authenticate user');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitLabel = isRegisterMode ? 'Create account' : 'Sign in';
    const busyLabel = isRegisterMode ? 'Creating account...' : 'Signing in...';

    const toggleMode = () => {
        if (isSubmitting) return;
        setError(null);
        setIsRegisterMode((prev) => !prev);
        setMessage(isRegisterMode ? 'Sign in to continue to your workspace.' : 'Create an account with a username and password.');
    };

    return (
        <main className="content-width mx-auto px-4 py-10 sm:px-6 sm:py-14">
            <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_440px]">
                <section className="surface-card rounded-[28px] px-6 py-8 sm:px-8 sm:py-10">
                    <span
                        className="inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase tracking-[0.18em]"
                        style={{ backgroundColor: 'var(--accent-tint)', color: 'var(--accent)' }}
                    >
                        AlgoX
                    </span>
                    <h1 className="mt-5 text-4xl font-semibold tracking-tight sm:text-5xl">Sign in to your trading workspace.</h1>
                    <p className="mt-4 max-w-xl text-base leading-7" style={{ color: 'var(--foreground-muted)' }}>
                        Track strategies, backtests, and market data freshness in one place.
                    </p>
                    <div className="mt-8 grid gap-3 sm:grid-cols-3">
                        {[
                            ['Market data', 'See how fresh every instrument feed is at a glance.'],
                            ['Strategies', 'Review and compare backtested strategies.'],
                            ['Ink & Signal', 'A calm, readable visual system with 11 colorways.'],
                        ].map(([title, description]) => (
                            <article key={title} className="surface-subtle rounded-3xl p-4">
                                <h2 className="text-sm font-semibold">{title}</h2>
                                <p className="mt-2 text-sm leading-6" style={{ color: 'var(--foreground-muted)' }}>
                                    {description}
                                </p>
                            </article>
                        ))}
                    </div>
                    <Link to="/" className="button-ghost mt-8 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-medium">
                        <ArrowLeft className="h-4 w-4" aria-hidden="true" />
                        Back to home
                    </Link>
                </section>

                <section className="surface-card rounded-[28px] p-6 sm:p-7">
                    <h2 className="text-2xl font-semibold">{isRegisterMode ? 'Create account' : 'Sign in'}</h2>
                    <p className="mt-2 text-sm leading-6" style={{ color: 'var(--foreground-muted)' }}>
                        {message}
                    </p>

                    <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                        <div>
                            <label htmlFor="username" className="text-sm font-semibold">
                                Username
                            </label>
                            <input
                                id="username"
                                type="text"
                                value={username}
                                onChange={(event) => setUsername(event.target.value)}
                                className="field mt-2 rounded-2xl px-4 py-3 text-sm"
                                placeholder="your-username"
                                minLength={3}
                                maxLength={64}
                                required
                            />
                        </div>

                        <div>
                            <label htmlFor="password" className="text-sm font-semibold">
                                Password
                            </label>
                            <input
                                id="password"
                                type="password"
                                value={password}
                                onChange={(event) => setPassword(event.target.value)}
                                className="field mt-2 rounded-2xl px-4 py-3 text-sm"
                                placeholder="Enter your password"
                                minLength={8}
                                maxLength={128}
                                required
                            />
                        </div>

                        <button type="submit" disabled={isSubmitting} className="button-primary w-full rounded-2xl px-4 py-3 text-sm font-semibold">
                            {isSubmitting ? busyLabel : submitLabel}
                        </button>
                    </form>

                    <button type="button" onClick={toggleMode} className="button-ghost mt-3 rounded-full px-4 py-2 text-sm font-medium">
                        {isRegisterMode ? 'Already have an account? Sign in' : 'Need an account? Create one'}
                    </button>

                    {error ? (
                        <p className="notice-error mt-4 rounded-3xl px-4 py-3 text-sm" aria-live="polite">
                            {error}
                        </p>
                    ) : null}
                </section>
            </div>
        </main>
    );
}
